import {Button, GrayButton} from '@/components/common-components/v2';
import {useStore} from '@/store/root-store';
import {LoadingOutlined} from '@ant-design/icons';
import {notification} from '@/utils/notification';
import {Checkbox, Dropdown, Popover, Spin} from 'antd';
import {observer} from 'mobx-react-lite';
import styled, {css, keyframes} from 'styled-components';
import {MissingPlacementsBanner} from './MissingPlacementsBanner';
import {convertHtmlToEditorState} from '@/utils/htmlToDraft';
import {LinksList} from './linksList';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus, faCaretDown} from '@fortawesome/pro-solid-svg-icons';
import React, {useEffect, useState} from 'react';
import {CustomTextarea} from '@/components/common-components/components/form/input/customTextarea';
import {stringToArray} from '@/utils/arrays';
import {keywordNewLineReg} from '@/constants/regex';
// import {NextImg} from '@/utils/nextImg';

export const LinkAssistantRightSidebar = observer(() => {
  const {
    contentOptimizer: {
      currentPage: {
        setIsLinkActive,
        setLinksForInsertion,
        editContent,
        stagingLinkSuggestions,
        setIsTaUpdating,
        updatingTa,
        clearAllTaData,
        setCurrentEditorState,
        updateContentLoadedAt,
        content,
        fetchLinkSuggestions,
        getstagingLinkSuggestions,
      },
    }} = useStore('');
  const {settings: {customer: {getCustomerQuota}}} = useStore('');
  const antIcon = <LoadingOutlined style={{fontSize: 20, marginLeft: 10}} spin />;
  const [activeKeywordsList, setActiveKeywordsList] = useState([]);
  const [updated, setUpdated] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [anyButtonClicked, setAnyButtonClicked] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(()=>{
    const activeKwArr = [];

    if (content?.internalLinkSuggestionsV2?.keywords?.length) {
      for (let i = 0; i < content.internalLinkSuggestionsV2.keywords.length; i++) {
        activeKwArr.push({
          keyword: content.internalLinkSuggestionsV2.keywords[i],
          active: true,
        });
      }
    }
    setActiveKeywordsList(activeKwArr);
  }, []);

  const onCheckboxClicked = (status: boolean, keyword: string) => {
    const filteredKw = activeKeywordsList;
    const elemId = filteredKw.findIndex(kw => kw.keyword === keyword);

    filteredKw[elemId].active = status;
    setActiveKeywordsList(filteredKw);
    setUpdated({kw: keyword, status: status});
  };

  const onCancelClickHandler = () => {
    setIsLinkActive(false);
    clearAllTaData();
    setLinksForInsertion([]);
  };

  const onSubmitHandler = async () => {
    setIsTaUpdating(true);
    let finalHtmlOutput = '';

    for (let i = 0; i < getstagingLinkSuggestions?.length; i++) {
      if (!getstagingLinkSuggestions[i].isSuggestion) {
        finalHtmlOutput = finalHtmlOutput.concat(getstagingLinkSuggestions[i].html.replace('</p>', '<br /></p>'));
      }

      if (getstagingLinkSuggestions[i].isSuggestion) {
        if (getstagingLinkSuggestions[i].status == 'accepted') {
          finalHtmlOutput = finalHtmlOutput.concat(getstagingLinkSuggestions[i].suggestion);
        } else {
          finalHtmlOutput = finalHtmlOutput.concat(getstagingLinkSuggestions[i].html.replace('</p>', '<br /></p>'));
        }
      }
    }

    if (finalHtmlOutput.startsWith('<NextImg')) finalHtmlOutput = '<p>&nbsp;</p>' + finalHtmlOutput;
    const updatedEditorState = convertHtmlToEditorState(finalHtmlOutput);
    try {
      await editContent(updatedEditorState, finalHtmlOutput == '');
      setCurrentEditorState(updatedEditorState);
      updateContentLoadedAt();

      setIsLinkActive(false);
      clearAllTaData();
    } catch (e) {
      return Promise.reject(e);
    } finally {
      setIsTaUpdating(false);
    }
  };

  const keywordsDropdown = <KeywordsDropdownContainer>
    <OverlayTitle>Show Internal Link Suggestions for following target keywords (does not affect manually provided URLs):</OverlayTitle>
    <div style={{display: 'flex', flexDirection: 'column'}} onClick={e => e.stopPropagation()}>
      {activeKeywordsList?.map((kw, id) => {
        return <CheckboxStyled key={id} defaultChecked={kw.active} onChange={e => onCheckboxClicked(e.target.checked, kw.keyword)}>{kw.keyword}</CheckboxStyled>;
      })}
    </div>
  </KeywordsDropdownContainer>;

  const isValidUrl = (urlString: string) => {
    try {
      new URL(urlString);
      return true;
    } catch (_) {
      return false;
    }
  };

  const addLink = async () => {
    if (inputValue?.length) {
      const allLink = content?.internalLinkSuggestionsV2?.allLinks?.map((item:any)=> item?.url);
      const links = stringToArray(inputValue, keywordNewLineReg, ',');
      setAnyButtonClicked(true);

      const invalidLinks = links.filter(link => !isValidUrl(link));

      if (invalidLinks.length > 0) {
        setAnyButtonClicked(false);
        return notification.error('Invalid URL:', `Please enter valid URLs. Invalid URLs: ${invalidLinks.join(', ')}`);
      }

      await fetchLinkSuggestions([...allLink, ...links]);

      getCustomerQuota();
      setAnyButtonClicked(false);
      setVisible(false);
      setUpdated({});
    }
    setInputValue('');
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e?.nativeEvent['data'] == ',') {
      setInputValue(e?.target?.value?.replace(',', '\n'));
    } else {
      setInputValue(e?.target?.value);
    }
  };

  const popoverContent = (
    <div>
      <OverlayTitle style={{marginTop: '4px'}}>Add more links you want to get suggestions for:</OverlayTitle>
      <CustomTextarea
        paddingToNumber='15px'
        paddingToText='12px'
        isKeyWordv2={true}
        autoSize={true}
        minHeight='131px'
        maxHeight='131px'
        width='400px'
        variant='customLinks'
        autoSizeCheck={true}
        placeholder='Enter urls separated by commas'
        onChange={handleInputChange}
        value={inputValue}
      ></CustomTextarea>
      <LinkSubmitBtns>
        <Button disabled={anyButtonClicked || inputValue.trim()?.length < 2} className='submitLink' onClick={addLink}>{anyButtonClicked && <Spin indicator={antIcon}/>} Submit links</Button>
        <Button onClick={()=> {
          setVisible(false);
          setInputValue('');
        }} className='dismissLink'>Dismiss</Button>
      </LinkSubmitBtns>
    </div>
  );


  const selectedLink = getstagingLinkSuggestions?.filter((item:any)=> item.status == 'accepted')?.length;

  return <TaWrapper>
    <TermsMainWrapper>
      <TitleContainer>
        <Title>Links Assistant</Title>
        <div style={{display: 'flex'}}>
          <Dropdown trigger={['click']} overlay={keywordsDropdown} placement='bottomCenter'>
            <ButtonStyled onClick={()=> setVisible(false)}>Keyword: <span>{activeKeywordsList?.filter(kw => kw.active)?.length}</span>/<span>{activeKeywordsList?.length}</span><FontAwesomeIcon icon={faCaretDown} style={{marginLeft: 5}}/></ButtonStyled>
          </Dropdown>
          <Popover
            trigger='click'
            visible={visible}
            content={popoverContent}
            placement='bottomLeft'
            overlayInnerStyle={{
              borderRadius: 5,
              background: '#fff',
              border: '1px solid #e8e8e8',
              boxShadow: '0px 6px 25px rgba(0, 0, 0, 0.15)',
              width: '450px',
            }}>
            <ButtonStyled onClick={()=> setVisible(true)}><FontAwesomeIcon icon={faPlus} style={{marginLeft: 5}}/> Add Links</ButtonStyled>
          </Popover>
        </div>
      </TitleContainer>
      <Description>
        <br />
      Review the suggested link placements to evaluate their fit and relevance within the context of your article.
        <br />
        {`To finalize and implement the changes, please click on 'Save Changes'.`}
      </Description>
      <MissingPlacementsBanner />
      <LinksList kwUpdate={updated} setVisible={setVisible} visible={visible}/>
    </TermsMainWrapper>
    <ActionButtonsContainer>
      {selectedLink > 0 ? <span>{`${selectedLink} suggestion${selectedLink > 1 ? 's' : ''} accepted`}</span> : <></>}
      <div style={{display: 'flex'}}>
        <GrayButton
          disabled={updatingTa}
          color='gray'
          variant='noBorder'
          style={{width: '-webkit-fill-available'}}
          onClick={() => onCancelClickHandler()}>
          {selectedLink > 0 ? 'Discard' : 'Cancel'}
        </GrayButton>
        <Button
          disabled={!stagingLinkSuggestions?.some(item => item.status !== 'default')}
          color='green'
          style={{width: '35%', justifyContent: 'center', display: 'flex'}}
          onClick={() => onSubmitHandler()}>
        Save changes
          {updatingTa ? <Spin indicator={antIcon} /> : null}
        </Button>
      </div>

    </ActionButtonsContainer>
  </TaWrapper>;
});

const TaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% - 0px);
  width: 30%;
  // padding-bottom: 20px;
  padding-right: 5%;
`;

const OverlayTitle = styled.p`
  color: #4E5156;
  font-size: 12px;
`;

const CheckboxStyled = styled(Checkbox)`
  margin-left: 0px !important;
  font-weight: 500 !important;
  // .ant-checkbox-inner{
  //   background-color: #2D6CCA !important;
  //   border-color: #2D6CCA !important;
  // }
`;

const TermsMainWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 5px;
text-align: left;
  // For Internet Explorer
  scrollbar-face-color: rgba(138,138,138,0.32);
  scrollbar-track-color: rgba(138,138,138,0.12);

  // For Google Chrome
  &::-webkit-scrollbar {
    height: 5px;
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #E8E8E8;
  }

  &::-webkit-scrollbar-track {
    background: rgba(138,138,138,0.12);
  }
`;
const ActionButtonsContainer = styled.div`
  background-color: #24262a;
  width: 100%;
  padding: 11px;
  display: flex; justify-content: space-between;
  align-items: center;
  border-radius: 14px;
  button{
    width: 63% !important;
  }
  span{
    color:white
  }
`;

const LinkSubmitBtns = styled.div`
display: flex;
margin-top: 15px;
.submitLink{
 margin-left: 0px;
  background-color: #2D6CCA !important;
  color: white;
  &:hover, &:focus, &:active{
  background-color: #2D6CCA !important;
  }
}
.dismissLink{
  background-color: #F2F2F5 !important;
  color: black;
  &:hover, &:focus, &:active{
  background-color: #F2F2F5 !important;
  }
}
`;


const ButtonStyled = styled.p<{viewSuggestionsOption?: boolean}>`
  background-color: transparent;
  border: 1px solid transparent;
  font-size: 13px;
  cursor: pointer;
  padding: 8px 3px;
  // margin: 0 9px 0 0 !important;
  color: #1890ff;
  &:hover, &:focus {
    background-color: transparent !important;
  }
  span{
    color: #1890ff;
  }
  animation: ${p => p.viewSuggestionsOption ? css`${blinkAnimation} 2s;` : ''}; 
  animation-delay: .5s;
  `;

const blinkAnimation = keyframes`
    0%, 100% {
    background-color: #2D2F34;
  }
  50% {
    background-color: #6F7177;
  }
`;
const KeywordsDropdownContainer = styled.div`
  background-color: #fff;
  box-shadow: 0px 6px 25px rgba(0, 0, 0, 0.15);
  width: 337px;
  padding: 12px 20px;
  border-radius: 5px;
  margin-right: 184px;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  .header{
    display: flex;
    width: 41%;
    justify-content: space-between;
    font-size: 12px;
    p{
      color: #2D6CCA
    }
  }
`;
const Title = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: black;
`;
const Description = styled.div`
  font-size: 12px;
  color: #6e6e6e;
  line-height: 15px;
  margin-bottom: 20px;
`;
